import { useCallback, useEffect, useRef, useState } from "preact/hooks";

import { ApiError, fetchMe, postLogout } from "../lib/api";
import { clearCachedIdentity, readCachedIdentity, writeCachedIdentity } from "../lib/meCache";
import { clearQueue } from "../offline/queue";
import type { MeOut } from "../types";

/**
 * Sprint-003 A5 session bootstrap, extended sprint-007 D4 for auth v2
 * (ADR-002 Amendment 4): `GET /api/v1/me` on mount decides which screen
 * the app shows. The old "there is no login form... the link itself IS
 * the login" docstring (`LoggedOutScreen.tsx`, retired this sprint) is
 * formally superseded — a person now reaches Hayloft independently via
 * `/login` (username + password) on every device, every time; a magic
 * link is onboarding/recovery-only.
 *
 * - `loading` — the initial `/me` call hasn't resolved yet.
 * - `authenticated` — a valid session; `name`/`canHandOutWork` drive the
 *   personalized greeting and the Team tab / hand-out-work visibility.
 *   `isAdmin` (sprint-004 B6 gap-fix) drives the Admin tab directly — see
 *   `hooks/useIsAdmin.ts`. `needsOnboarding`/`passwordResetRequired`
 *   (sprint-007 D4) are the auth-v2 state-machine flags `app.tsx` routes
 *   an authenticated session on BEFORE ever rendering the main app:
 *   right after any magic-link consume exactly one is true (never both),
 *   forcing `views/CredentialSetupView.tsx` in the matching mode; a
 *   normal password login carries neither and goes straight to Today.
 *   `passwordSetByAdmin` (TD-058, ADR-002 Amendment 12 §11.3) is the
 *   discriminator INSIDE `passwordResetRequired` — true iff an admin set
 *   this password and the user has not settled it yet — and is what
 *   `app.tsx` picks `"must-change"` vs `"reset"` on. It is a server read,
 *   so unlike the page-lifecycle proxy it replaced it survives a full app
 *   restart. `username`/`hasPasskey` are carried for completeness
 *   (settings/passkey UI is sprint-007 D5/D6, out of this hook's scope).
 *
 *   `offline` (barn dead zone) — true when `/me` could not be reached on
 *   this load and the identity came from the last successful `/me` this
 *   device saw (`lib/meCache.ts`). The app shell, the service-worker-
 *   cached Today list, and the offline banner render exactly as they
 *   would online; the hook re-checks `/me` quietly when the browser
 *   reports it is back online, and a 401 at that point signs the device
 *   out as usual. An offline identity never carries the onboarding/reset
 *   flags (a session that still owes one of those steps is never cached).
 * - `unauthenticated` — `/me` 401'd (no session cookie, or an
 *   expired/revoked one) — `views/LoginView.tsx` (replaces the retired
 *   `LoggedOutScreen`'s bare "open your link" message).
 * - `error` — a genuine network/connectivity failure reaching `/me`
 *   itself (distinct from "no session" — retryable) with NO cached
 *   identity to fall back on (first-ever open on this device, or the
 *   cache was cleared by a logout/401). `consecutiveFailures` (sprint-006
 *   D5, F-D4-3) counts how many *in a row* — a real farm dead zone (Flint
 *   Creek/back-pasture, per the intake notes) can mean several "Try
 *   again" taps in a row with no other feedback that anything is
 *   different from a broken app; `ConnectionErrorScreen` uses this to add
 *   a weak-signal-framed line after the second consecutive failure.
 */
export type MeState =
  | { status: "loading" }
  | {
      status: "authenticated";
      id: string;
      name: string;
      username: string | null;
      canHandOutWork: boolean;
      isAdmin: boolean;
      needsOnboarding: boolean;
      passwordResetRequired: boolean;
      passwordSetByAdmin: boolean;
      hasPasskey: boolean;
      /** True when this identity came from the offline cache, not a live
       * `/me` read. Optional so existing `MeState` fixtures stay valid;
       * absent means live. */
      offline?: boolean;
    }
  | { status: "unauthenticated" }
  | { status: "error"; message: string; consecutiveFailures: number };

type AuthenticatedState = Extract<MeState, { status: "authenticated" }>;

function fromMeOut(me: MeOut): AuthenticatedState {
  return {
    status: "authenticated",
    id: me.id,
    name: me.name,
    username: me.username,
    canHandOutWork: me.can_hand_out_work,
    isAdmin: me.is_admin,
    needsOnboarding: me.needs_onboarding,
    passwordResetRequired: me.password_reset_required,
    passwordSetByAdmin: me.password_set_by_admin,
    hasPasskey: me.has_passkey,
  };
}

/** Persist (or drop) the offline identity after a successful live `/me`.
 * A session that still owes onboarding or a password reset is never
 * cached, so an offline cold start can't route around those screens. */
function rememberIdentity(state: AuthenticatedState): void {
  if (state.needsOnboarding || state.passwordResetRequired) {
    clearCachedIdentity();
    return;
  }
  writeCachedIdentity({
    id: state.id,
    name: state.name,
    username: state.username,
    canHandOutWork: state.canHandOutWork,
    isAdmin: state.isAdmin,
    hasPasskey: state.hasPasskey,
  });
}

/** The cached identity as an `authenticated` state flagged `offline`, or
 * `null` when this device has none. */
function offlineIdentity(): AuthenticatedState | null {
  const cached = readCachedIdentity();
  if (!cached) return null;
  return {
    status: "authenticated",
    id: cached.id,
    name: cached.name,
    username: cached.username,
    canHandOutWork: cached.canHandOutWork,
    isAdmin: cached.isAdmin,
    needsOnboarding: false,
    passwordResetRequired: false,
    passwordSetByAdmin: false,
    hasPasskey: cached.hasPasskey,
    offline: true,
  };
}

/** "We couldn't reach Hayloft" as opposed to "Hayloft answered no".
 * `fetch()` rejecting (no signal, DNS, refused) is the barn case; a
 * 502/503/504 is the reverse proxy saying the same thing about the app
 * behind it. A 401/403 or any other 4xx is a real answer and never falls
 * back to the cache. */
function isUnreachable(err: unknown): boolean {
  if (!(err instanceof ApiError)) return true;
  if (err.isNetworkError) return true;
  return err.status === 502 || err.status === 503 || err.status === 504;
}

/** How often an offline identity re-checks `/me` while it stays offline. */
export const OFFLINE_RECHECK_MS = 60_000;

/** Service-worker runtime cache holding the last `/api/v1/due` (see
 * `vite.config.ts`). */
const DUE_CACHE_NAME = "hayloft-due-cache";

async function clearDeviceData(): Promise<void> {
  try {
    await clearQueue();
  } catch {
    // IndexedDB unavailable — nothing was queued on this device.
  }
  try {
    if (typeof caches !== "undefined") await caches.delete(DUE_CACHE_NAME);
  } catch {
    // Cache Storage unavailable (non-secure context) — nothing cached.
  }
}

export function useMe(): {
  state: MeState;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
} {
  const [state, setState] = useState<MeState>({ status: "loading" });
  const consecutiveFailuresRef = useRef(0);
  // Bumped by `logout`. A `/me` read that started before a sign-out must
  // never land after it — it would re-cache the identity and flip state
  // back to authenticated on a phone someone just signed out of.
  const generationRef = useRef(0);

  /** Reads `/me` and settles state. `quiet` (the back-online re-check)
   * skips the `loading` flash and leaves an offline identity in place if
   * the network is still not there. */
  const load = useCallback(async (quiet: boolean) => {
    if (!quiet) setState({ status: "loading" });
    const generation = generationRef.current;
    try {
      const next = fromMeOut(await fetchMe());
      if (generation !== generationRef.current) return;
      consecutiveFailuresRef.current = 0;
      rememberIdentity(next);
      setState(next);
    } catch (err) {
      if (generation !== generationRef.current) return;
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        // `lib/api.ts` already clears on any 401; a 403 on `/me` itself is
        // equally a "this identity is not valid here" answer.
        clearCachedIdentity();
      }
      if (err instanceof ApiError && err.status === 401) {
        consecutiveFailuresRef.current = 0;
        setState({ status: "unauthenticated" });
        return;
      }
      if (isUnreachable(err)) {
        const cached = offlineIdentity();
        if (cached) {
          consecutiveFailuresRef.current = 0;
          setState(cached);
          return;
        }
        if (quiet) return;
      }
      consecutiveFailuresRef.current += 1;
      setState({
        status: "error",
        message: "Couldn't reach Hayloft. Check your connection and try again.",
        consecutiveFailures: consecutiveFailuresRef.current,
      });
    }
  }, []);

  const refresh = useCallback(() => load(false), [load]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Offline identity: re-check `/me` so a recovered server, a revoked
  // session or a role change is picked up without restarting the app. The
  // `online` event alone isn't enough — behind a captive portal, or after
  // a 502/503 during a redeploy, `navigator.onLine` was true all along and
  // that event never fires. So also re-check when the app comes back to
  // the foreground, and on a slow timer while the identity stays offline.
  const isOfflineIdentity = state.status === "authenticated" && state.offline === true;
  useEffect(() => {
    if (!isOfflineIdentity || typeof window === "undefined") return undefined;
    const recheck = () => void load(true);
    const handleVisible = () => {
      if (document.visibilityState === "visible") recheck();
    };
    window.addEventListener("online", recheck);
    document.addEventListener("visibilitychange", handleVisible);
    const timer = window.setInterval(recheck, OFFLINE_RECHECK_MS);
    return () => {
      window.removeEventListener("online", recheck);
      document.removeEventListener("visibilitychange", handleVisible);
      window.clearInterval(timer);
    };
  }, [isOfflineIdentity, load]);

  const logout = useCallback(async () => {
    // Cleared first and unconditionally: signing out must stick on this
    // device even if the network call below never reaches the server.
    generationRef.current += 1;
    clearCachedIdentity();
    // A shared farm phone: the next person must not inherit this person's
    // unsent taps (they would replay under the NEW session) or their
    // cached Today list. `SignOutButton` warns first when taps are queued.
    await clearDeviceData();
    try {
      await postLogout();
    } catch {
      // Best-effort — even if the network call fails, treat this device
      // as logged out locally; the cookie may already be gone/invalid.
    }
    setState({ status: "unauthenticated" });
  }, []);

  return { state, refresh, logout };
}
